
import { Target, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface RiskScoreGaugeProps {
  score: number;
  confidence?: number;
  size?: "small" | "medium" | "large";
  showDetails?: boolean;
  className?: string;
}

const RiskScoreGauge = ({ 
  score, 
  confidence = 85, 
  size = "large", 
  showDetails = true,
  className = "" 
}: RiskScoreGaugeProps) => {
  const getRiskLevel = (score: number) => {
    if (score >= 80) return { level: "High Priority", color: "red", icon: AlertTriangle, bg: "bg-red-500" };
    if (score >= 60) return { level: "Elevated Risk", color: "orange", icon: AlertTriangle, bg: "bg-orange-500" };
    if (score >= 40) return { level: "Moderate Risk", color: "yellow", icon: TrendingUp, bg: "bg-yellow-500" };
    if (score >= 20) return { level: "Minimal Concern", color: "blue", icon: Target, bg: "bg-blue-500" };
    return { level: "Very Low Risk", color: "green", icon: CheckCircle, bg: "bg-green-500" };
  };

  const risk = getRiskLevel(score);
  const IconComponent = risk.icon;
  
  const sizeClasses = {
    small: "w-24 h-24 text-lg",
    medium: "w-32 h-32 text-2xl",
    large: "w-40 h-40 text-4xl"
  };

  const getConfidenceColor = (conf: number) => {
    if (conf >= 80) return "text-green-600 bg-green-100";
    if (conf >= 60) return "text-blue-600 bg-blue-100";
    return "text-yellow-600 bg-yellow-100";
  };

  return (
    <Card className={`p-6 bg-white border border-border/20 shadow-sm ${className}`}>
      <div className="flex flex-col items-center space-y-4">
        {/* Clinical Score Display */}
        <div className="relative">
          {/* Outer ring container */}
          <div className={`${sizeClasses[size]} rounded-full bg-muted/10 border-2 border-border/30 flex items-center justify-center relative`}>
            {/* Progress ring using SVG for precision */}
            <svg 
              className="absolute inset-0 -rotate-90" 
              width="100%" 
              height="100%" 
              viewBox="0 0 120 120"
            >
              {/* Background circle */}
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="hsl(var(--muted))"
                strokeWidth="3"
                className="opacity-20"
              />
              {/* Progress circle */}
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke={risk.bg.replace('bg-', 'hsl(var(--')}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${score * 3.27} 327`}
                className="transition-all duration-700 ease-out"
                style={{
                  stroke: risk.color === 'red' ? 'hsl(0, 84%, 60%)' :
                          risk.color === 'orange' ? 'hsl(25, 95%, 53%)' :
                          risk.color === 'yellow' ? 'hsl(45, 93%, 47%)' :
                          risk.color === 'blue' ? 'hsl(217, 91%, 60%)' :
                          'hsl(142, 71%, 45%)'
                }}
              />
            </svg>
            
            {/* Center content */}
            <div className="flex flex-col items-center justify-center z-10">
              <span className={`font-bold text-foreground ${
                size === 'large' ? 'text-3xl' :
                size === 'medium' ? 'text-2xl' : 'text-xl'
              }`}>
                {score}
              </span>
              <span className={`text-muted-foreground font-medium ${
                size === 'large' ? 'text-sm' :
                size === 'medium' ? 'text-xs' : 'text-xs'
              }`}>/100</span>
            </div>
          </div>
          
          {/* Risk level indicator badge */}
          <div className={`absolute -top-1 -right-1 p-1.5 rounded-full border-2 border-background shadow-sm ${
            risk.color === 'red' ? 'bg-red-500' :
            risk.color === 'orange' ? 'bg-orange-500' :
            risk.color === 'yellow' ? 'bg-yellow-500' :
            risk.color === 'blue' ? 'bg-blue-500' :
            'bg-green-500'
          }`}>
            <IconComponent className="h-3 w-3 text-white" />
          </div>
        </div>

        {/* Clinical Risk Assessment */}
        <div className="text-center space-y-2">
          <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${
            risk.color === 'red' ? 'bg-red-50 text-red-700 border-red-200' :
            risk.color === 'orange' ? 'bg-orange-50 text-orange-700 border-orange-200' :
            risk.color === 'yellow' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
            risk.color === 'blue' ? 'bg-blue-50 text-blue-700 border-blue-200' :
            'bg-green-50 text-green-700 border-green-200'
          }`}>
            {risk.level}
          </div>
        </div>

        {/* Confidence Indicator */}
        {showDetails && (
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${getConfidenceColor(confidence).includes('green') ? 'bg-green-500' : 
                                                      getConfidenceColor(confidence).includes('blue') ? 'bg-blue-500' : 'bg-yellow-500'}`} />
              <span className="text-xs text-muted-foreground font-medium">
                {confidence}% Confidence
              </span>
            </div>
          </div>
        )}

        {/* Clinical Reference Scale */}
        {showDetails && size === "large" && (
          <div className="w-full max-w-sm space-y-2">
            <div className="text-center">
              <span className="text-xs font-medium text-muted-foreground">Risk Assessment Scale</span>
            </div>
            
            {/* Scale bar */}
            <div className="relative h-3 bg-muted rounded-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 via-yellow-400 via-orange-400 to-red-500 rounded-full" />
              
              {/* Score indicator */}
              <div 
                className="absolute top-0 w-1 h-3 bg-foreground rounded-full transform -translate-x-1/2 shadow-sm"
                style={{ left: `${score}%` }}
              />
            </div>
            
            {/* Scale labels */}
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className="font-medium">0</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span className="font-medium">100</span>
            </div>
            
            {/* Risk level descriptions */}
            <div className="grid grid-cols-4 gap-1 text-xs text-center mt-2">
              <span className="text-green-600 font-medium">Very Low</span>
              <span className="text-yellow-600 font-medium">Moderate</span>
              <span className="text-orange-600 font-medium">Elevated</span>
              <span className="text-red-600 font-medium">High</span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default RiskScoreGauge;
